/**
 * Génère et télécharge le PDF d'une facture, cotation ou avoir.
 * Utilisée par : SingleTransferSale, SingleTransferAvoir, ListTransferSale, ListTransferQuote, ListTransferAvoir.
 */
import jsPDFInvoiceTemplate, { OutputType } from "jspdf-invoice-template-nodejs";

function getLogoAsBase64(shop) {
  return new Promise((resolve, reject) => {
    if (!shop?.logo?.filename) {
      reject(new Error('Shop logo filename is missing'));
      return;
    }
    const logoFilename = shop.logo.filename;
    const logoPath = `/stocks/photos/${logoFilename}`;
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        const targetWidth = 40;
        const targetHeight = targetWidth / aspectRatio;

        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        let base64Image;
        try {
          base64Image = canvas.toDataURL('image/png');
        } catch (e) {
          base64Image = canvas.toDataURL('image/jpeg', 0.95);
        }

        resolve({
          src: base64Image,
          width: targetWidth,
          height: targetHeight,
          type: base64Image.startsWith('data:image/png') ? 'PNG' : 'JPEG'
        });
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load logo from: ${logoPath}`));
    };

    img.src = logoPath;
  });
}

function calculateTotals(trsf) {
  let totalHT = 0;
  let totalTVA = 0;
  let totalTTC = 0;
  const aibAmount = parseFloat(trsf.aib || 0) || 0;
  const productList = Array.isArray(trsf.products) ? trsf.products : [];

  productList.forEach((saleItem) => {
    let itemHT = parseFloat(saleItem.total_ht || saleItem.price_ht || 0);
    let itemTTC = parseFloat(saleItem.total_ttc || saleItem.price_ttc || 0);
    let tvaRate = saleItem.tva_rate || 0;

    if (tvaRate !== 0 && itemTTC === itemHT) {
      itemTTC = itemHT * (1 + tvaRate / 100);
    }

    let itemTVA = itemTTC - itemHT;
    totalHT += itemHT;
    totalTVA += itemTVA;
    totalTTC += itemTTC;
  });

  return {
    totalHT,
    totalTVA,
    totalTTC,
    aib: aibAmount,
    totalToPay: totalTTC + aibAmount,
  };
}

function createTableOfProduct(trsf, products, showTVA) {
  if (!trsf.products || !Array.isArray(trsf.products)) return [];
  return trsf.products.map((saleItem, index) => {
    let prod = products && products[saleItem.product_id];
    let totalHT = parseFloat(saleItem.total_ht || saleItem.price_ht || 0);
    let totalTTC = parseFloat(saleItem.total_ttc || saleItem.price_ttc || 0);
    let tvaRate = saleItem.tva_rate || 0;

    if (tvaRate !== 0 && totalTTC === totalHT) {
      totalTTC = totalHT * (1 + tvaRate / 100);
    }

    let PUHT = totalHT / saleItem.quantity;

    if (showTVA) {
      return [
        index + 1,
        prod?.ref || `REF-${(saleItem.product_id || '').substring(0, 8)}`,
        prod?.name || `Product ID: ${saleItem.product_id}`,
        parseFloat(PUHT.toFixed(2)),
        saleItem.quantity,
        parseFloat(totalHT.toFixed(2)),
        `${tvaRate} %`,
        parseFloat(totalTTC.toFixed(2)),
      ];
    }
    return [
      index + 1,
      prod?.ref || `REF-${(saleItem.product_id || '').substring(0, 8)}`,
      prod?.name || `Product ID: ${saleItem.product_id}`,
      parseFloat(PUHT.toFixed(2)),
      saleItem.quantity,
      parseFloat(totalHT.toFixed(2)),
    ];
  });
}

function getLabelsForNature(nature) {
  const n = (nature || '').toLowerCase();
  if (n === 'avoir') {
    return {
      contactLabel: 'Avoir pour :',
      invoiceLabel: 'Avoir n° ',
      invDateLabel: 'Date Avoir: ',
      invGenDateLabel: 'Date Avoir: ',
      totalLabel: 'Total Avoir',
    };
  }
  if (n === 'quote') {
    return {
      contactLabel: 'Devis pour :',
      invoiceLabel: 'Devis n° ',
      invDateLabel: 'Date Cotation: ',
      invGenDateLabel: 'Date Mise à jour stock: ',
      totalLabel: 'Total à payer',
    };
  }
  return {
    contactLabel: 'Facture pour :',
    invoiceLabel: 'Facture n° ',
    invDateLabel: 'Date Paiement: ',
    invGenDateLabel: 'Date Facture: ',
    totalLabel: 'Total à payer',
  };
}

/**
 * Génère et télécharge le PDF d'une facture, cotation ou avoir.
 * @param {Object} params
 * @param {Object} params.transferDetails - Détails complets (fetchTransferDetails)
 * @param {Object} params.shop - Magasin (SHOPS[id])
 * @param {Object} params.client - Client (CLIENTS[id]) ou {} si absent
 * @param {Object} params.products - Catalogue produits (PRODUCTS)
 * @param {Object} params.options - { showTVA: true, currency: "FCFA" }
 * @param {Function} params.onSuccess - Callback optionnel en cas de succès
 * @param {Function} params.onError - Callback optionnel en cas d'erreur
 */
export async function generateTransferPDF({
  transferDetails: trsf,
  shop,
  client = {},
  products = {},
  options = {},
  onSuccess,
  onError,
}) {
  const { showTVA = true, currency = 'FCFA' } = options;

  if (!trsf || !shop) {
    const err = new Error('transferDetails and shop are required');
    if (onError) onError(err);
    throw err;
  }

  const totals = calculateTotals(trsf);
  let logoData = null;
  try {
    logoData = await getLogoAsBase64(shop);
  } catch (err) {
    console.warn('[WARN] Logo not available, PDF will be generated without logo');
  }

  const invoiceNumber = trsf.intern_ref_id || trsf.id || 'unknown';
  const labels = getLabelsForNature(trsf.nature);

  const clientFirstName = client?.client_firstname || '';
  const clientLastName = (client?.client_lastname || '').toUpperCase();
  const clientFullName = [clientFirstName, clientLastName].filter(Boolean).join(' ') || 'Client';

  const headerColumns = [
    { title: '#', style: { width: 10 } },
    { title: 'Code', style: { width: showTVA ? 25 : 35 } },
    { title: 'Nom', style: { width: showTVA ? 55 : 80 } },
    { title: 'Prix HT', style: { width: showTVA ? 20 : 23 } },
    { title: 'Quantité', style: { width: showTVA ? 18 : 20 } },
    { title: 'Total HT', style: { width: 25 } },
  ];
  if (showTVA) {
    headerColumns.push(
      { title: 'TVA', style: { width: 15 } },
      { title: 'Total TTC', style: { width: 25 } }
    );
  }

  const additionalRows = showTVA
    ? [
        { col1: 'Total produits TTC:', col2: String(totals.totalTTC.toFixed(2)), col3: currency, style: { fontSize: 10 } },
        { col1: 'AIB/BIC:', col2: String(totals.aib.toFixed(2)), col3: currency, style: { fontSize: 10 } },
        { col1: labels.totalLabel + ':', col2: String(totals.totalToPay.toFixed(2)), col3: currency, style: { fontSize: 14 } },
        { col1: 'TVA:', col2: String(totals.totalTVA.toFixed(2)), col3: currency, style: { fontSize: 10 } },
        { col1: 'Sous-total HT:', col2: String(totals.totalHT.toFixed(2)), col3: currency, style: { fontSize: 10 } },
      ]
    : [
        { col1: 'Total produits HT:', col2: String(totals.totalHT.toFixed(2)), col3: currency, style: { fontSize: 10 } },
        { col1: 'AIB/BIC:', col2: String(totals.aib.toFixed(2)), col3: currency, style: { fontSize: 10 } },
        { col1: labels.totalLabel + ':', col2: String(totals.totalToPay.toFixed(2)), col3: currency, style: { fontSize: 14 } },
      ];

  const prefix = trsf.nature === 'avoir' ? 'avoir' : 'facture';
  const invoice = {
    outputType: OutputType.Save,
    retsurnJsPDFDocObject: true,
    fileName: `${prefix}-${invoiceNumber}.pdf`,
    orientationLandscape: false,
    compress: true,
  };

  if (logoData) {
    invoice.logo = {
      src: logoData.src,
      type: logoData.type,
      width: logoData.width,
      height: logoData.height,
      margin: { top: 0, left: 0 },
    };
  }

  invoice.stamp = {
    inAllPages: true,
    src: 'https://raw.githubusercontent.com/edisonneza/jspdf-invoice-template/demo/images/qr_code.jpg',
    type: 'JPEG',
    width: 20,
    height: 20,
    margin: { top: 0, left: 0 },
  };

  invoice.business = {
    name: String(shop.name || ''),
    address: [shop.address, shop.city, shop.country].filter(Boolean).join(' ') || '',
    phone: String(shop.tel || ''),
    email: String(shop.email || ''),
  };

  invoice.contact = {
    label: labels.contactLabel,
    name: clientFullName,
    address: String(client?.client_address || ''),
    phone: String(client?.client_tel || ''),
    otherInfo: [client?.client_city, client?.client_country].filter(Boolean).join(' - ') || '',
  };

  invoice.invoice = {
    label: labels.invoiceLabel,
    num: invoiceNumber,
    invDate: labels.invDateLabel + (new Date(trsf.creation_date)).toLocaleString('fr-FR'),
    invGenDate: labels.invGenDateLabel + (trsf.nature === 'quote' ? (new Date()).toLocaleString('fr-FR') : (new Date(trsf.creation_date)).toLocaleString('fr-FR')),
    headerBorder: true,
    tableBodyBorder: true,
    header: headerColumns,
    table: createTableOfProduct(trsf, products, showTVA),
    additionalRows,
    invDescLabel: 'Note',
    invDesc: trsf.comments || '',
    styles: { cellPadding: 5, minCellHeight: 18, lineColor: [0, 0, 0], lineWidth: 0.3 },
    headerStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', minCellHeight: 20, cellPadding: 5 },
    bodyStyles: { minCellHeight: 18, cellPadding: 5 },
  };

  invoice.footer = {
    text: [shop.bottom_info, shop.bottom_info2, shop.bottom_info3].filter(Boolean).join('\n') || '',
  };

  invoice.pageEnable = true;
  invoice.pageLabel = 'Page ';

  try {
    jsPDFInvoiceTemplate(invoice);
    if (onSuccess) onSuccess();
  } catch (err) {
    console.error('[ERROR] PDF generation failed:', err.message);
    if (onError) onError(err);
    throw err;
  }
}
